import { useState } from "react";
import axiosInstance from "./index";
import { ApiResponseType } from "./type";

export type AuthApiType = {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
};

const authApiDefaultValue = {
  accessToken: localStorage.getItem("accessToken") || "",
  refreshToken: localStorage.getItem("refreshToken") || "",
  tokenType: "",
  expiresIn: 0,
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  role: "",
};

export type LoginArgsType = {
  email: string;
  otp: number | null;
};

export type AuthApiReturnType = {
  onLogin: (values: LoginArgsType) => Promise<ApiResponseType>;
  onLogout: () => void;
  fetchUserProfile: () => Promise<ApiResponseType>;
  getOtp: (email: string) => Promise<ApiResponseType>;
  authData: AuthApiType;
  setAuthData: any;
  isAuthLoading: boolean;
  loginLoading: boolean;
  logoutLoading: boolean;
};

const useAuthApi = (props: any): AuthApiReturnType => {
  const { snackbarCtx } = props;
  const [authApiData, setAuthApiData] = useState<AuthApiType>(authApiDefaultValue);
  const [authLoading, toggleAuthLoading] = useState<boolean>(false);
  const [loginLoading, toggleLoginLoading] = useState(false);
  const [logoutLoading, toggleLogoutLoading] = useState(false);

  const onLogin = async (values: LoginArgsType): Promise<ApiResponseType> => {
    try {
      const { email, otp } = values;
      toggleLoginLoading(true);
      const response = await axiosInstance.post("auth/login", {
        email,
        otp,
      });
      if (response.data.status === 201) {
        localStorage.setItem("accessToken", response.data.result.token);
        localStorage.setItem("refreshToken", response.data.result.refreshToken);
        const payload = {
          accessToken: response.data.result.token,
          refreshToken: response.data.result.refreshToken,
          expiresIn: response.data.result.tokenExpiryTime,
        };
        setAuthApiData((data) => ({ ...data, ...payload }));
        snackbarCtx.toggleSnackbar({
          message: "Login Successful",
          type: "SUCCESS",
          visible: true,
        });
        return {
          success: response.data.status === 201,
          data: response.data.result,
          message: response.data.message,
        };
      }

      return {
        success: false,
        data: null,
        message: response.data.message,
      };
    } catch (err: any) {
      toggleLoginLoading(false);
      return {
        success: false,
        data: null,
        errorField: err.response?.data?.errorfields,
        customErrorNumber: err.response?.data?.customErrorNumber,
        message: err.response?.data?.message || "Something went wrong. Please try again",
      };
    } finally {
      toggleLoginLoading(false);
    }
  };

  const onLogout = async (): Promise<ApiResponseType> => {
    const refresh_token = localStorage.getItem("refreshToken");

    try {
      toggleLogoutLoading(true);
      const response = await axiosInstance.post("auth/logout", {
        refreshToken:refresh_token,
      });
      localStorage.clear();
      setAuthApiData(authApiDefaultValue);
      window.location.href = "/login";
      localStorage.clear();
      return {
        success: response.data.status === 201,
        data: response.data.result,
        message: response.data.message,
      };
    } catch (err: any) {
      localStorage.clear();
      window.location.href = "/login";
      return {
        success: false,
        data: null,
        errorField: err.response?.data?.errorfields,
        message: err.response?.data?.message || "Something went wrong. Please try again",
      };
    } finally {
      toggleLogoutLoading(false);
    }
  };

  const fetchUserProfile = async (): Promise<ApiResponseType> => {
    try {
      toggleAuthLoading(true);
      const response: any = await axiosInstance.get("/staff");

      const updatedData = {
        accessToken: authApiData.accessToken,
        refreshToken: authApiData.refreshToken,
        tokenType: authApiData.tokenType,
        expiresIn: authApiData.expiresIn,
        id: response.data.result.id,
        firstName: response.data.result.firstName,
        lastName: response.data.result.lastName,
        email: response.data.result.email,
        role: response.data.result.role,
        // username: response.data.result.username,
        // profileImage: response.data.result.profileImage,
      };

      setAuthApiData(updatedData);
      return {
        success: response.status === 200,
        data: updatedData, // Return the updated data
        message: response.data.message,
      };
    } catch (err: any) {
      return {
        success: false,
        data: null,
        message: err.message,
      };
    } finally {
      toggleAuthLoading(false);
    }
  };

  const getOtp = async (email: string) => {
    try {
      toggleLoginLoading(true);
      const response = await axiosInstance.post("auth/send-otp", { email });
      return {
        success: response.data.status === 201,
        data: response.data.result,
        message: response.data.message,
      };
    } catch (err: any) {
      toggleLoginLoading(false);
      return {
        success: false,
        data: err?.response?.data?.customErrorNumber || 6969,
        message: err?.response?.data?.message || 'Something went wrong !',
      };
    } finally {
      toggleLoginLoading(false);
    }

  }

  return {
    authData: authApiData,
    isAuthLoading: authLoading,
    setAuthData: setAuthApiData,
    onLogin,
    onLogout,
    fetchUserProfile,
    getOtp,
    logoutLoading,
    loginLoading: loginLoading,
  };
};

export default useAuthApi;
